import { createFileRoute, Link, Navigate, useRouter } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import apiClient from '@/api/client'
import type { LoginRequest, LoginResponse, APIResponse } from '@/types'
import {
  Eye,
  EyeOff,
  Lock,
  User as UserIcon,
  ScanBarcode,
  PackageCheck,
  Calculator,
  FileCheck2,
  Loader2,
} from 'lucide-react'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  const router = useRouter()
  const [formData, setFormData] = useState<LoginRequest>({ username: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response: APIResponse<LoginResponse> = await apiClient.login(credentials)
      return response
    },
    onSuccess: (data) => {
      if (data.success && data.data) {
        apiClient.setAuthToken(data.data.token)
        localStorage.setItem('pos_user', JSON.stringify(data.data.user))
        setTimeout(() => {
          router.navigate({ to: '/' })
        }, 100)
      } else {
        setError(data.message || 'Login failed')
      }
    },
    onError: (err: any) => {
      setError(err.message || 'Login failed')
    },
  })

  // Already authenticated → home (must run after all hooks)
  if (apiClient.isAuthenticated()) {
    return <Navigate to="/" />
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!formData.username.trim() || !formData.password) {
      setError('Username or email and password are required')
      return
    }
    loginMutation.mutate({
      username: formData.username.trim(),
      password: formData.password,
    })
  }

  return (
    <div className="bhookly-login mt-[var(--desktop-update-banner-h)] h-[calc(100vh_-_var(--desktop-update-banner-h))] w-full overflow-y-auto bg-[var(--bhk-surface)] text-[color:var(--bhk-text)] lg:grid lg:grid-cols-[3fr_2fr] lg:grid-rows-1">
      <BrandPanel />
      <FormPanel
        formData={formData}
        setFormData={setFormData}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        error={error}
        isPending={loginMutation.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Wordmark — the B mark plus "Bhookly Retail".
// ─────────────────────────────────────────────────────────────────────────────
function Wordmark({ tone = 'light' as 'light' | 'surface' }) {
  const onPanel = tone === 'light'
  return (
    <div className="flex items-center gap-3">
      <div
        className={`grid h-10 w-10 place-items-center rounded-lg ${
          onPanel ? 'bg-white text-[#0B4A37]' : 'bg-[var(--bhk-cta)] text-[color:var(--bhk-cta-text)]'
        }`}
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M7 4v16" />
          <path d="M7 4h5.5a3.5 3.5 0 0 1 0 7H7" />
          <path d="M7 11h6.5a3.5 3.5 0 0 1 0 9H7" />
        </svg>
      </div>
      <div className={`bhk-display flex items-baseline gap-1.5 text-2xl leading-none ${onPanel ? 'text-white' : ''}`}>
        <span className="font-extrabold">Bhookly</span>
        <span className="font-normal opacity-80">Retail</span>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// LEFT PANEL — what the product does, and a real-looking receipt from a shop
// using it. The receipt prints once on load; nothing else moves.
// ─────────────────────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: ScanBarcode, text: 'Scan a barcode or tap a product to ring up a sale.' },
  { icon: PackageCheck, text: 'Stock comes off the shelf count as soon as a sale is paid.' },
  { icon: Calculator, text: 'Close the day by counting the drawer against what it should hold.' },
  { icon: FileCheck2, text: 'Send invoices to FBR or PRA once your shop is registered.' },
]

function BrandPanel() {
  return (
    <aside className="bhk-panel relative hidden overflow-hidden text-white lg:flex lg:flex-col">
      <div className="relative z-10 px-10 pt-10 xl:px-14">
        <Wordmark tone="light" />
      </div>

      <div className="relative z-10 flex flex-1 items-center px-10 py-10 xl:px-14">
        <div className="max-w-[17rem] xl:max-w-[22rem] 2xl:max-w-[28rem]">
          <h1 className="bhk-display text-[40px] font-extrabold leading-[1.04] tracking-[-0.02em] [text-wrap:balance] xl:text-[52px] 2xl:text-[60px]">
            Your shop, counted to the last rupee.
          </h1>
          <p className="mt-5 text-[15px] leading-relaxed text-white/75 xl:text-base">
            Bhookly Retail is the till, stock register and day-end book for kiryana stores, pharmacies,
            electronics and clothing shops across Pakistan.
          </p>

          <ul className="mt-8 space-y-3.5">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex gap-3 text-[14px] leading-snug text-white/90">
                <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-[#F5C518]" aria-hidden />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* The receipt hangs out of a printer slot at the top edge. */}
      <div className="pointer-events-none absolute right-6 top-0 z-0 origin-top-right scale-[0.7] xl:right-14 xl:scale-90 2xl:right-[4.5rem] 2xl:scale-100">
        <div className="mx-auto h-3 w-[316px] rounded-b-md bg-black/35 shadow-[inset_0_-2px_0_#F5C518]" />
        <div className="mx-auto w-[300px] overflow-hidden">
          <Receipt />
        </div>
      </div>

      <div className="relative z-10 px-10 pb-8 text-xs text-white/60 xl:px-14">
        <FooterCredit tone="light" />
      </div>
    </aside>
  )
}

const RECEIPT_ITEMS = [
  { name: 'Cooking oil 1 L', qty: 2, price: 575 },
  { name: 'Tea 475 g', qty: 1, price: 1090 },
  { name: 'Detergent 1 kg', qty: 1, price: 640 },
  { name: 'Shampoo 360 ml', qty: 1, price: 820 },
  { name: 'Toothpaste 150 g', qty: 2, price: 294 },
]

const rs = (n: number) =>
  n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function Receipt() {
  const printedAt = useMemo(() => {
    const d = new Date()
    const p = (n: number) => String(n).padStart(2, '0')
    return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
  }, [])

  const subtotal = RECEIPT_ITEMS.reduce((s, i) => s + i.qty * i.price, 0)
  const tax = Math.round(subtotal * 0.18 * 100) / 100
  const posFee = 1
  const total = subtotal + tax + posFee
  const cash = Math.ceil(total / 100) * 100
  const count = RECEIPT_ITEMS.reduce((s, i) => s + i.qty, 0)

  return (
    <div className="bhk-receipt bhk-print relative px-5 pb-9 pt-5 text-[12px] leading-[1.55]" aria-hidden>
      <div className="text-center">
        {/* Placeholder identity on purpose — never a real shop name, NTN or
            FBR invoice number on an illustration. */}
        <div className="text-[13px] font-semibold">YOUR SHOP NAME</div>
        <div>Your address, City</div>
        <div>NTN 0000000-0</div>
      </div>

      <Rule />
      <Line left={`Sale 000482`} right={printedAt} />
      <Line left="Cashier" right="Ayesha" />
      <Rule />

      {RECEIPT_ITEMS.map((item) => (
        <div key={item.name}>
          <div>{item.name}</div>
          <Line left={`  ${item.qty} × ${rs(item.price)}`} right={rs(item.qty * item.price)} />
        </div>
      ))}

      <Rule />
      <Line left={`Subtotal (${count} items)`} right={rs(subtotal)} />
      <Line left="Sales tax 18%" right={rs(tax)} />
      <Line left="FBR POS fee" right={rs(posFee)} />
      <div className="my-1 flex justify-between text-[15px] font-semibold">
        <span>Total</span>
        <span>Rs {rs(total)}</span>
      </div>
      <Line left="Cash" right={rs(cash)} />
      <Line left="Change" right={rs(cash - total)} />
      <Rule />

      <div className="flex items-start gap-3 pt-1">
        <FauxQr />
        <div className="text-[11px] leading-[1.45]">
          <div className="font-semibold">FBR invoice</div>
          <div>000000000000</div>
          <div>000000000000</div>
          <div className="mt-1">Verify on the Tax Asaan app</div>
        </div>
      </div>
      <div className="mt-3 text-center">Thank you for shopping with us</div>
    </div>
  )
}

function Line({ left, right }: { left: string; right: string }) {
  return (
    <div className="flex justify-between gap-3 whitespace-pre">
      <span>{left}</span>
      <span>{right}</span>
    </div>
  )
}

function Rule() {
  return <div className="my-1.5 border-t border-dashed border-[#1E2622]/40" />
}

// A QR-shaped illustration (finder squares + deterministic pattern). It is
// decoration on a sample receipt, not a scannable code.
const FAUX_QR_PATH = (() => {
  const n = 25
  const cells: boolean[][] = Array.from({ length: n }, () => Array(n).fill(false))
  let seed = 482
  const rand = () => {
    seed = (seed * 1103515245 + 12345) % 2147483648
    return seed / 2147483648
  }
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) cells[y][x] = rand() > 0.52
  const finder = (ox: number, oy: number) => {
    for (let y = -1; y < 8; y++)
      for (let x = -1; x < 8; x++) {
        const cx = ox + x
        const cy = oy + y
        if (cx < 0 || cy < 0 || cx >= n || cy >= n) continue
        const ring = x === 0 || x === 6 || y === 0 || y === 6
        const core = x >= 2 && x <= 4 && y >= 2 && y <= 4
        cells[cy][cx] = x >= 0 && x <= 6 && y >= 0 && y <= 6 && (ring || core)
      }
  }
  finder(0, 0)
  finder(n - 7, 0)
  finder(0, n - 7)
  let d = ''
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (cells[y][x]) d += `M${x} ${y}h1v1h-1z`
  return d
})()

function FauxQr() {
  return (
    <svg viewBox="-1 -1 27 27" className="h-[74px] w-[74px] shrink-0" shapeRendering="crispEdges">
      <path d={FAUX_QR_PATH} fill="#1E2622" />
    </svg>
  )
}

// Footer credit line. Hovering releases a small flock of emojis that hop just
// above the text — playful but small enough not to dominate the footer.
function FooterCredit({ tone = 'light' as 'light' | 'surface' }) {
  const linkClass =
    tone === 'light'
      ? 'font-medium text-white underline-offset-2 hover:underline'
      : 'font-medium text-[color:var(--bhk-cta)] underline-offset-2 hover:underline'

  return (
    <div className="bhk-credit relative inline-flex items-center gap-1 whitespace-nowrap">
      {/* Emojis hover above the line. Pointer-events disabled so they never
          steal hover from the text underneath. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-3 right-0 flex select-none gap-1.5"
      >
        <span className="bhk-emoji" style={{ animationDelay: '0ms' }}>🎨</span>
        <span className="bhk-emoji" style={{ animationDelay: '70ms' }}>✨</span>
        <span className="bhk-emoji" style={{ animationDelay: '140ms' }}>🛒</span>
        <span className="bhk-emoji" style={{ animationDelay: '210ms' }}>💫</span>
      </span>

      <span>
        Built with <span aria-label="love">🤍</span> by{' '}
        <a
          href="https://artyreal.com"
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          Artyreal
        </a>
      </span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// RIGHT PANEL — the sign-in form. Quiet on purpose: this is opened at the
// till every morning, so it stays fast to read and fast to fill.
// ─────────────────────────────────────────────────────────────────────────────
type FormPanelProps = {
  formData: LoginRequest
  setFormData: React.Dispatch<React.SetStateAction<LoginRequest>>
  showPassword: boolean
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>
  error: string
  isPending: boolean
  onSubmit: (e: React.FormEvent) => void
}

function FormPanel({
  formData,
  setFormData,
  showPassword,
  setShowPassword,
  error,
  isPending,
  onSubmit,
}: FormPanelProps) {
  return (
    <section className="relative flex h-full flex-col overflow-y-auto bg-[var(--bhk-surface)] px-6 py-8 sm:px-10 lg:px-14 lg:py-10">
      {/* wordmark + one-line summary when the brand panel is hidden */}
      <div className="mb-10 lg:hidden">
        <Wordmark tone="surface" />
        <p className="mt-3 max-w-sm text-sm text-[color:var(--bhk-muted)]">
          Point of sale, stock and daily closing for shops across Pakistan.
        </p>
      </div>

      <div className="hidden items-center justify-end gap-2 text-[13px] text-[color:var(--bhk-muted)] lg:flex">
        <span>Setting up a new shop?</span>
        <a
          href="mailto:hello@bhookly.com"
          className="bhk-link font-semibold text-[color:var(--bhk-cta)] underline-offset-2 hover:underline"
        >
          Talk to our team
        </a>
      </div>

      <div className="flex flex-1 items-center justify-center">
        <div className="w-full max-w-[400px]">
          <div className="mb-8">
            <h2 className="bhk-display text-[34px] font-extrabold leading-[1.08] tracking-[-0.015em]">
              Sign in
            </h2>
            <p className="mt-2 text-[15px] leading-relaxed text-[color:var(--bhk-muted)]">
              Use the username or email and password your shop’s admin gave you.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <Field label="Username or email">
              <div className="bhk-input flex h-12 items-center rounded-lg px-3.5">
                <UserIcon className="mr-2.5 h-4 w-4 text-[color:var(--bhk-muted)]" />
                <Input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData((p) => ({ ...p, username: e.target.value }))}
                  className="h-full border-0 bg-transparent px-0 text-[15px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  autoComplete="username"
                  placeholder="e.g. ayesha"
                  disabled={isPending}
                />
              </div>
            </Field>

            <Field
              label="Password"
              right={
                <Link
                  to="/forgot-password"
                  className="bhk-link text-[13px] font-medium text-[color:var(--bhk-muted)] underline-offset-2 hover:text-[color:var(--bhk-cta)] hover:underline"
                  tabIndex={-1}
                >
                  Forgot password?
                </Link>
              }
            >
              <div className="bhk-input flex h-12 items-center rounded-lg px-3.5">
                <Lock className="mr-2.5 h-4 w-4 text-[color:var(--bhk-muted)]" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                  className="h-full border-0 bg-transparent px-0 text-[15px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  disabled={isPending}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="ml-1 grid h-8 w-8 place-items-center rounded-md text-[color:var(--bhk-muted)] hover:bg-[var(--bhk-hover)] hover:text-[color:var(--bhk-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--bhk-focus)]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[13px] text-rose-800 dark:border-rose-900/70 dark:bg-rose-950/40 dark:text-rose-200"
              >
                <span className="mt-0.5 grid h-4 w-4 flex-shrink-0 place-items-center rounded-full bg-rose-600 text-[10px] font-bold text-white">
                  !
                </span>
                <div>
                  <div className="font-semibold">Couldn't sign you in</div>
                  <div className="text-rose-700 dark:text-rose-300">{error}</div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="bhk-cta h-12 w-full rounded-lg border-0 text-[15px] font-semibold"
              disabled={isPending}
            >
              {isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </span>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <p className="mt-6 text-[13px] text-[color:var(--bhk-muted)] lg:hidden">
            Setting up a new shop?{' '}
            <a
              href="mailto:hello@bhookly.com"
              className="bhk-link font-semibold text-[color:var(--bhk-cta)] underline-offset-2 hover:underline"
            >
              Talk to our team
            </a>
          </p>
        </div>
      </div>

      {/* footer (below lg the brand panel, and its footer, are hidden) */}
      <div className="mt-8 flex items-center justify-center text-[11px] text-[color:var(--bhk-muted)] lg:hidden">
        <FooterCredit tone="surface" />
      </div>
    </section>
  )
}

function Field({
  label,
  right,
  children,
}: {
  label: string
  right?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[14px] font-medium">{label}</span>
        {right}
      </div>
      {children}
    </label>
  )
}
